/**
 * Displays pending loan requests for items the current user owns or holds.
 * Allows the user to approve or reject each request. Uses the shared
 * `REQUEST_CONFLICT_MESSAGE` sentinel to detect race conditions where a
 * request was already processed by the time the user responds.
 */
import { usePendingRequestsForMe, useRespondToRequest } from '@/hooks/useLoanRequests';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

/**
 *
 */
export function LoanRequestsList() {
  const { data: requests, isLoading, isError, refetch } = usePendingRequestsForMe();
  const respondToRequest = useRespondToRequest();

  const handleRespond = async (requestId: string, approved: boolean) => {
    try {
      await respondToRequest.mutateAsync({ requestId, approved });
      toast.success(approved ? 'Request approved!' : 'Request rejected');
    } catch {
      // Error toast handled by useRespondToRequest onError
    }
  };

  if (isLoading) {
    return (
      <div style={{ fontSize: 14, color: T.textMuted, padding: '8px 0' }}>Loading requests...</div>
    );
  }

  if (isError) {
    return (
      <div style={{ fontSize: 14, color: T.danger, padding: '8px 0' }}>
        Failed to load requests.{' '}
        <button
          onClick={() => refetch()}
          style={{
            background: 'none',
            border: 'none',
            color: T.accent,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            padding: 0,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.md,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px 12px',
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, color: T.text }}>Pending Loan Requests</div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: RADIUS.dot,
            background: T.accent,
            color: T.white,
            letterSpacing: '0.03em',
          }}
        >
          {requests.length}
        </span>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 13, color: T.textMuted }}>
          People are requesting to borrow your items
        </div>
      </div>

      {/* Request list */}
      <div style={{ padding: '12px 16px 16px' }}>
        {requests.map((request, idx) => (
          <div
            key={request.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: 12,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.sm,
              marginBottom: idx < requests.length - 1 ? 8 : 0,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 2 }}>
                {request.requester_name} wants to borrow{' '}
                <span style={{ color: T.accent }}>{request.item_name}</span>
              </div>
              {request.message && (
                <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 2 }}>
                  "{request.message}"
                </div>
              )}
              <div style={{ fontSize: 12, color: T.textMuted }}>
                {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {/* Reject */}
              <button
                onClick={() => handleRespond(request.id, false)}
                disabled={respondToRequest.isPending}
                aria-label="Reject"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: RADIUS.sm,
                  border: `1px solid ${T.border}`,
                  background: T.surface,
                  cursor: respondToRequest.isPending ? 'default' : 'pointer',
                  color: T.textSec,
                  opacity: respondToRequest.isPending ? 0.6 : 1,
                }}
              >
                <X size={14} />
              </button>
              {/* Approve */}
              <button
                onClick={() => handleRespond(request.id, true)}
                disabled={respondToRequest.isPending}
                aria-label="Approve"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: 'none',
                  background: T.accent,
                  boxShadow: SHADOW.btn.accent,
                  cursor: respondToRequest.isPending ? 'default' : 'pointer',
                  color: T.white,
                  opacity: respondToRequest.isPending ? 0.6 : 1,
                }}
              >
                <Check size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
